// The Comms page's view of Dropbox folder drift.
//
// Two surfaces, both fed by comms.press_folder_alerts:
//   FolderDriftBanner  on the projects list — "these projects changed in
//                      Dropbox", so drift is noticed without opening anything.
//   FolderAlertsPanel  in a project's press kit, next to "Check Dropbox
//                      structure", where the fix lives.
//
// Wording and ordering are in folderAlerts.js; this file is presentation and the
// two writes a person can make.
import React, { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, ChevronRight, RefreshCw } from 'lucide-react';
import { S, GOLD } from './commsUi';
import {
  alertKind, describeAlert, fetchOpenFolderAlerts, groupAlertsByProject,
  moveInstruction, pressRelativePath, setFolderAlertStatus, summarizeAlerts,
} from './folderAlerts';

const TONES = {
  error: { bg: '#FDF3F2', border: '#E2B5AE', text: '#8C3A2C' },
  warn:  { bg: '#FEF9E7', border: '#E8C84A', text: '#7A5C00' },
};

const tone = t => TONES[t] || TONES.warn;

/**
 * Open alerts, either for one project or for all of them.
 *
 * `rev` is the caller's refresh counter, so fixing a folder elsewhere on the
 * page re-reads this list rather than leaving a resolved alert on screen.
 */
export function useFolderAlerts(projectCode = null, rev = 0) {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [localRev, setLocalRev] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchOpenFolderAlerts(projectCode)
      .then(rows => { if (!cancelled) { setAlerts(rows); setError(null); } })
      .catch(e => { if (!cancelled) setError(e.message || String(e)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [projectCode, rev, localRev]);

  return { alerts, loading, error, reload: useCallback(() => setLocalRev(r => r + 1), []) };
}

const btn = {
  background: 'none', border: '1px solid rgba(27,27,25,.2)', borderRadius: 3,
  fontFamily: "'IBM Plex Mono', monospace", fontSize: 10, letterSpacing: '0.08em',
  textTransform: 'uppercase', color: '#6E6C66', cursor: 'pointer', padding: '3px 8px',
};

/**
 * The undo, written out rather than performed.
 *
 * A move is inferred from a deletion and a creation that share a name, so the
 * app describes what to do and leaves the doing to a person — moving somebody's
 * folders on an inference is a far worse way to be wrong than saying the wrong
 * sentence. The full destination path is offered for copying, because that is
 * what you paste into Dropbox's search to find the folder.
 */
function MoveInstruction({ alert }) {
  const [copied, setCopied] = useState(false);
  const instruction = moveInstruction(alert);
  const here = pressRelativePath(alert.detail?.to || alert.path);
  if (!instruction) return null;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(alert.detail?.to || alert.path);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      // Clipboard is blocked in some contexts; the path is on screen anyway.
    }
  };

  return (
    <div style={{ marginTop: 5, padding: '6px 9px', background: 'rgba(255,255,255,.55)', borderRadius: 3, border: '1px solid rgba(27,27,25,.08)' }}>
      <div style={{ fontSize: 12, color: '#1B1B19', lineHeight: 1.5 }}>{instruction}</div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4, flexWrap: 'wrap' }}>
        <code style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 10.5, color: '#6E6C66', wordBreak: 'break-all' }}>{here}</code>
        <button onClick={copy} style={{ ...btn, padding: '2px 7px' }}>{copied ? 'Copied' : 'Copy full path'}</button>
      </div>
    </div>
  );
}

function AlertRow({ alert, onAct, busy, canFix, onFix }) {
  const kind = alertKind(alert.kind);
  const c = tone(kind.tone);
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: '7px 0', borderTop: '1px solid rgba(27,27,25,.08)' }}>
      <AlertTriangle size={12} style={{ flexShrink: 0, marginTop: 3, color: c.text }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 12.5, color: c.text, lineHeight: 1.5 }}>{describeAlert(alert)}</div>
        {kind.hint && <div style={{ ...S.meta, fontSize: 11, marginTop: 2 }}>{kind.hint}</div>}
        {alert.kind === 'moved' && <MoveInstruction alert={alert} />}
      </div>
      <div style={{ display: 'flex', gap: 5, flexShrink: 0 }}>
        {canFix && alert.kind === 'missing' && (
          <button onClick={onFix} disabled={busy} style={{ ...btn, color: GOLD, borderColor: GOLD }}>Create</button>
        )}
        {/* Resolve says "I've handled it" and lets the next scan disagree.
            Dismiss says "this is correct" and is permanent for this folder. */}
        <button onClick={() => onAct(alert, 'resolved')} disabled={busy} style={btn}>Resolve</button>
        <button onClick={() => onAct(alert, 'dismissed')} disabled={busy} style={btn}>Dismiss</button>
      </div>
    </div>
  );
}

/**
 * A project's open drift, shown in its press kit.
 *
 * `onCreateMissing` is the same "Create missing" the manual audit offers; it is
 * wired through rather than reimplemented so both routes provision folders the
 * same way.
 */
export function FolderAlertsPanel({ projectCode, rev = 0, canEdit = false, onCreateMissing, showToast }) {
  const { alerts, loading, error, reload } = useFolderAlerts(projectCode, rev);
  const [busy, setBusy] = useState(false);

  const act = async (alert, status) => {
    setBusy(true);
    try {
      await setFolderAlertStatus(alert.id, status);
      reload();
      showToast?.(status === 'dismissed' ? 'Dismissed — this folder will not be flagged again' : 'Marked as handled');
    } catch (e) {
      showToast?.(e.message || String(e), 'error');
    } finally {
      setBusy(false);
    }
  };

  const fix = async () => {
    setBusy(true);
    try {
      await onCreateMissing?.();
      reload();
    } finally {
      setBusy(false);
    }
  };

  if (loading || error || alerts.length === 0) return null;

  const worst = tone(alertKind(alerts[0].kind).tone);

  return (
    <div style={{ background: worst.bg, border: `1px solid ${worst.border}`, borderRadius: 3, padding: '9px 12px', marginBottom: 10 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
        <div style={{ ...S.eyebrow, fontSize: 10, color: worst.text }}>
          Changed in Dropbox since the last check
        </div>
        <button onClick={reload} style={{ ...btn, border: 'none', padding: 0, display: 'flex', alignItems: 'center', gap: 4 }}>
          <RefreshCw size={10} /> Refresh
        </button>
      </div>
      {alerts.map(a => (
        <AlertRow key={a.id} alert={a} onAct={act} busy={busy}
          canFix={canEdit && !!onCreateMissing} onFix={fix} />
      ))}
    </div>
  );
}

/**
 * The projects list's headline. Silent when nothing has drifted — a banner that
 * is always there stops being read.
 */
export function FolderDriftBanner({ rev = 0, onOpenProject, projectNames = {} }) {
  const { alerts, loading, error } = useFolderAlerts(null, rev);
  if (loading || error || alerts.length === 0) return null;

  const summary = summarizeAlerts(alerts);
  const c = tone(summary.tone);

  return (
    <div style={{ background: c.bg, border: `1px solid ${c.border}`, borderRadius: 3, padding: '10px 14px', marginBottom: 16 }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
        <AlertTriangle size={13} style={{ flexShrink: 0, marginTop: 2, color: c.text }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, color: c.text, lineHeight: 1.5 }}>{summary.text}</div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 7 }}>
            {groupAlertsByProject(alerts).map(({ project_code, alerts: list }) => (
              <button
                key={project_code}
                onClick={() => onOpenProject?.(project_code)}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 4,
                  background: 'rgba(255,255,255,.6)', border: `1px solid ${c.border}`,
                  borderRadius: 20, padding: '3px 9px', cursor: 'pointer',
                  fontFamily: "'IBM Plex Mono', monospace", fontSize: 10,
                  letterSpacing: '0.06em', color: c.text,
                }}
              >
                {project_code}
                {projectNames[project_code] ? ` · ${projectNames[project_code]}` : ''}
                <span style={{ opacity: 0.7 }}>({list.length})</span>
                <ChevronRight size={10} />
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
